import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { QuickBookService } from "./Service";

export const MAPPING_DEFAULTS = {
  serviceId: "",
  providerId: null,
  providerServiceId: null,
  coverageCenterAddressId: null,
  coverageRadiusKm: 8,
  priority: 100,
  providerUnitPayoutRupees: "",
  payoutEligible: true,
  scheduleManagedByCudel: false,
  displayNameMode: "EXPERT",
  displayNameOverride: "",
  active: true,
  previewQuantity: 1,
};

export const toQuickBookMappingPayload = (values, expectedVersion) => {
  const payload = {
    serviceId: values.serviceId,
    providerId: Number(values.providerId),
    providerServiceId: Number(values.providerServiceId),
    coverageCenterAddressId: Number(values.coverageCenterAddressId),
    coverageRadiusKm: Number(values.coverageRadiusKm),
    priority: Number(values.priority),
    providerUnitPayoutPaise: Math.round(Number(values.providerUnitPayoutRupees) * 100),
    payoutEligible: Boolean(values.payoutEligible),
    scheduleManagedByCudel: Boolean(values.scheduleManagedByCudel),
    displayNameMode: values.displayNameMode,
    displayNameOverride: values.displayNameMode === "CUSTOM" ? values.displayNameOverride.trim() : null,
    active: Boolean(values.active),
  };
  return Number.isSafeInteger(expectedVersion) ? { ...payload, expectedVersion } : payload;
};

export const isQuickBookMappingEditUnsafe = (mapping) => (
  Boolean(mapping) && !Number.isSafeInteger(mapping.version)
);

export const isValidCoverageAddress = (address) => {
  const coordinates = address?.coordinates?.coordinates;
  const longitude = Array.isArray(coordinates) ? Number(coordinates[0]) : Number(address?.longitude);
  const latitude = Array.isArray(coordinates) ? Number(coordinates[1]) : Number(address?.latitude);
  return String(address?.serviceArea) === "1"
    && Number.isFinite(latitude) && latitude >= -90 && latitude <= 90
    && Number.isFinite(longitude) && longitude >= -180 && longitude <= 180;
};

export const compatibleQuickBookServices = (services, providerServices) => {
  const serviceMasterIds = new Set(providerServices.map((service) => Number(service.servicesChildId)));
  return services.filter((service) => serviceMasterIds.has(Number(service.serviceMasterId)));
};

const integerRules = (min, max) => ({
  required: "Required",
  min: { value: min, message: `Minimum ${min}` },
  max: { value: max, message: `Maximum ${max}` },
  validate: (value) => Number.isInteger(Number(value)) || "Whole numbers only",
});

const ok = (response) => response?.success === 1 || response?.success === "1";

export default function QuickBookMappingForm({ open, mapping, onClose, onSaved, onConflict }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState("");
  const [providers, setProviders] = useState([]);
  const [providerSearch, setProviderSearch] = useState("");
  const [providerServices, setProviderServices] = useState([]);
  const [loadedProviderId, setLoadedProviderId] = useState(null);
  const [providerServicesError, setProviderServicesError] = useState("");
  const [providerDetail, setProviderDetail] = useState(null);
  const [loadingProvider, setLoadingProvider] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const { control, handleSubmit, reset, setError, setValue, watch } = useForm({
    mode: "onChange",
    defaultValues: MAPPING_DEFAULTS,
  });
  const serviceId = watch("serviceId");
  const providerId = watch("providerId");
  const providerServiceId = watch("providerServiceId");
  const displayNameMode = watch("displayNameMode");
  const scheduleManagedByCudel = watch("scheduleManagedByCudel");
  const payoutRupees = watch("providerUnitPayoutRupees");
  const previewQuantity = watch("previewQuantity");

  useEffect(() => {
    if (!open) return;
    setMessage(null);
    reset(mapping ? {
      ...MAPPING_DEFAULTS,
      ...mapping,
      providerUnitPayoutRupees: (Number(mapping.providerUnitPayoutPaise || 0) / 100).toFixed(2),
      displayNameOverride: mapping.displayNameOverride || "",
    } : MAPPING_DEFAULTS);
  }, [mapping, open, reset]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setServicesLoading(true);
    setServicesError("");
    setServices([]);
    QuickBookService.list({ page: 1, limit: 100, search: "", mode: "", status: "" }).then((response) => {
      if (!active) return;
      if (ok(response)) {
        setServices((response.data?.items || []).filter((service) => service.status !== "ARCHIVED"));
      } else {
        setServicesError(response?.message || "Quick Book service options could not be loaded.");
      }
      setServicesLoading(false);
    });
    return () => { active = false; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    const timer = setTimeout(async () => {
      const response = await QuickBookService.expertSettings({ page: 1, limit: 50, search: providerSearch.trim(), status: "" });
      if (active && ok(response)) setProviders(response.data?.items || []);
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [open, providerSearch]);

  useEffect(() => {
    if (!open || !providerId) {
      setProviderServices([]);
      setLoadedProviderId(null);
      setProviderServicesError("");
      setProviderDetail(null);
      return;
    }
    let active = true;
    setLoadingProvider(true);
    setLoadedProviderId(null);
    setProviderServicesError("");
    Promise.all([
      QuickBookService.providerServices(providerId),
      QuickBookService.providerDetail(providerId),
    ]).then(([serviceResponse, detailResponse]) => {
      if (!active) return;
      setProviderServices(ok(serviceResponse) ? serviceResponse.data?.list || [] : []);
      setProviderServicesError(ok(serviceResponse) ? "" : serviceResponse?.message || "Expert services could not be loaded.");
      setProviderDetail(ok(detailResponse) ? detailResponse.data : null);
      setLoadedProviderId(Number(providerId));
      setLoadingProvider(false);
    }).catch(() => {
      if (!active) return;
      setProviderServices([]);
      setProviderDetail(null);
      setProviderServicesError("Expert services could not be loaded.");
      setLoadedProviderId(Number(providerId));
      setLoadingProvider(false);
    });
    return () => { active = false; };
  }, [open, providerId]);

  const selectedService = services.find((service) => service.serviceId === serviceId);
  const providerLoaded = Number(loadedProviderId) === Number(providerId);
  const availableQuickBookServices = useMemo(() => (
    providerId ? compatibleQuickBookServices(services, providerServices) : []
  ), [providerId, providerServices, services]);
  const quickBookServiceOptions = !providerLoaded && selectedService
    ? [selectedService]
    : availableQuickBookServices;
  const matchingProviderServices = useMemo(() => providerServices.filter((service) => (
    Number(service.servicesChildId) === Number(selectedService?.serviceMasterId)
  )), [providerServices, selectedService]);
  const coverageAddresses = useMemo(() => (
    (providerDetail?.userAllAddress || []).filter(isValidCoverageAddress)
  ), [providerDetail]);
  const selectedProvider = providers.find((provider) => Number(provider.providerId) === Number(providerId))
    || (mapping && Number(mapping.providerId) === Number(providerId) ? {
      providerId: mapping.providerId,
      displayName: mapping.providerDisplayName,
    } : null);

  useEffect(() => {
    if (!selectedService || !providerLoaded) return;
    if (providerServiceId && !matchingProviderServices.some((service) => Number(service.id) === Number(providerServiceId))) {
      setValue("providerServiceId", null, { shouldValidate: true });
    } else if (!providerServiceId && matchingProviderServices.length === 1) {
      setValue("providerServiceId", matchingProviderServices[0].id, { shouldValidate: true });
    }
  }, [matchingProviderServices, providerLoaded, providerServiceId, selectedService, setValue]);

  const money = (paise) => new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(paise || 0) / 100);
  const payoutPaise = Math.round(Number(payoutRupees || 0) * 100);
  const quantity = Math.max(1, Number(previewQuantity || 1));
  const customerUnitPaise = Number(selectedService?.unitAmountPaise || 0);
  const negativeMargin = payoutPaise > customerUnitPaise;
  const bankReady = Boolean(providerDetail?.UserBankDetail?.accountNumber && providerDetail?.UserBankDetail?.ifscCode);
  const payoutReady = Array.isArray(providerDetail?.providerPayments) && providerDetail.providerPayments.length > 0;

  const submit = async (values) => {
    setSaving(true);
    setMessage(null);
    const response = mapping
      ? await QuickBookService.updateMapping(mapping.mappingId, toQuickBookMappingPayload(values, mapping.version))
      : await QuickBookService.createMapping(toQuickBookMappingPayload(values));
    setSaving(false);
    if (ok(response)) {
      onSaved(response.data);
      return;
    }
    (response?.error?.fieldErrors || []).forEach(({ field, message: fieldMessage }) => {
      setError(field === "providerUnitPayoutPaise" ? "providerUnitPayoutRupees" : field, { type: "server", message: fieldMessage });
    });
    const stale = response?.error?.code === "QUICK_BOOK_CONFIG_VERSION_CHANGED";
    if (stale) onConflict();
    setMessage({
      severity: stale || response?.error?.retryable ? "warning" : "error",
      text: stale ? "This mapping changed. Close and reopen it before retrying." : response?.message || "Mapping could not be saved.",
    });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullScreen={fullScreen} fullWidth maxWidth="lg" aria-labelledby="quick-book-mapping-title">
      <DialogTitle id="quick-book-mapping-title">{mapping ? "Edit" : "Add"} Quick Book mapping</DialogTitle>
      <DialogContent dividers>
        <Box component="form" id="quick-book-mapping-form" onSubmit={handleSubmit(submit)} noValidate>
          <Stack spacing={2}>
            {message && <Alert severity={message.severity}>{message.text}</Alert>}
            {servicesError && <Alert severity="error">Service options unavailable: {servicesError}</Alert>}
            {providerServicesError && <Alert severity="error">{providerServicesError}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="providerId"
                  control={control}
                  rules={{ required: "Select an expert" }}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      options={selectedProvider && !providers.some((provider) => Number(provider.providerId) === Number(selectedProvider.providerId)) ? [selectedProvider, ...providers] : providers}
                      value={selectedProvider}
                      getOptionLabel={(provider) => `${provider.displayName || "Expert"} (#${provider.providerId})`}
                      isOptionEqualToValue={(option, value) => Number(option.providerId) === Number(value.providerId)}
                      onInputChange={(_, value, reason) => reason === "input" && setProviderSearch(value)}
                      onChange={(_, value) => {
                        field.onChange(value?.providerId || null);
                        setProviderServices([]);
                        setProviderDetail(null);
                        setLoadedProviderId(null);
                        setProviderServicesError("");
                        setValue("serviceId", "");
                        setValue("providerServiceId", null);
                        setValue("coverageCenterAddressId", null);
                      }}
                      renderInput={(params) => <TextField {...params} label="Expert" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message || "Search by name or mobile"} />}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="serviceId"
                  control={control}
                  rules={{ required: "Select a Quick Book service" }}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      options={quickBookServiceOptions}
                      loading={servicesLoading || loadingProvider}
                      disabled={!providerId || !providerLoaded || servicesLoading || loadingProvider || Boolean(servicesError) || Boolean(providerServicesError)}
                      value={selectedService || null}
                      getOptionLabel={(service) => service.customerTitle || service.serviceId}
                      isOptionEqualToValue={(option, value) => option.serviceId === value.serviceId}
                      noOptionsText="This expert has no compatible Quick Book services"
                      onChange={(_, value) => {
                        field.onChange(value?.serviceId || "");
                        setValue("providerServiceId", null);
                      }}
                      renderInput={(params) => <TextField {...params} label="Quick Book service" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message || (!providerId ? "Select an expert first" : "Only services offered by this expert are shown")} />}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {selectedProvider && (
              <Alert severity={selectedProvider.accountActive === false ? "warning" : "info"}>
                Readiness: account {selectedProvider.accountActive ? "active" : "inactive"}; review {selectedProvider.readiness?.reviewApproved ? "approved" : "not approved"}; KYC {selectedProvider.readiness?.kycVerified ? "verified" : "not verified"}; PAN {selectedProvider.readiness?.panVerified ? "verified" : "not verified"}; bank {bankReady ? "available" : "not ready"}; payout setup {payoutReady ? "available" : "not ready"}. The backend makes the final eligibility decision.
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="providerServiceId"
                  control={control}
                  rules={{
                    required: "Select the matching provider service",
                    validate: (value) => matchingProviderServices.some((service) => Number(service.id) === Number(value)) || "Provider service must match this expert and canonical service",
                  }}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      options={matchingProviderServices}
                      disabled={!serviceId || !providerLoaded || Boolean(providerServicesError)}
                      value={matchingProviderServices.find((service) => Number(service.id) === Number(field.value)) || null}
                      loading={loadingProvider}
                      getOptionLabel={(service) => `${service.childService?.title || `Service #${service.servicesChildId}`}${service.brd?.title ? ` · ${service.brd.title}` : ""}`}
                      isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                      onChange={(_, value) => field.onChange(value?.id || null)}
                      renderInput={(params) => <TextField {...params} label="Expert service record" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message || (!providerId ? "Select an expert first" : !serviceId ? "Select a Quick Book service first" : "Choose the exact matching service or brand variant")} />}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="coverageCenterAddressId"
                  control={control}
                  rules={{
                    required: "Select a valid provider service-area address",
                    validate: (value) => coverageAddresses.some((address) => Number(address.id) === Number(value)) || "Coverage centre must be a valid address owned by this expert",
                  }}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      options={coverageAddresses}
                      value={coverageAddresses.find((address) => Number(address.id) === Number(field.value)) || null}
                      loading={loadingProvider}
                      getOptionLabel={(address) => [address.address, address.city, address.pincode].filter(Boolean).join(", ") || `Address #${address.id}`}
                      isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                      onChange={(_, value) => field.onChange(value?.id || null)}
                      renderInput={(params) => <TextField {...params} label="Coverage centre" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message || "Provider-owned service address with valid coordinates"} />}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Controller
                  name="coverageRadiusKm"
                  control={control}
                  rules={{ required: "Required", min: { value: 0.01, message: "Must be greater than 0" }, max: { value: 1000, message: "Maximum 1000" } }}
                  render={({ field, fieldState }) => <TextField {...field} type="number" fullWidth label="Coverage radius (km)" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message} inputProps={{ min: 0.01, max: 1000, step: 0.01 }} />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Controller
                  name="priority"
                  control={control}
                  rules={integerRules(0, 1000000)}
                  render={({ field, fieldState }) => <TextField {...field} type="number" fullWidth label="Priority" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message || "Lower numbers rank first"} inputProps={{ min: 0, max: 1000000, step: 1 }} />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Controller
                  name="providerUnitPayoutRupees"
                  control={control}
                  rules={{ required: "Required", min: { value: 0, message: "Minimum 0" }, validate: (value) => /^\d+(\.\d{1,2})?$/.test(String(value)) || "Use at most 2 decimal places" }}
                  render={({ field, fieldState }) => <TextField {...field} type="number" fullWidth label="Provider unit payout (₹)" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message || "Configured independently from customer price"} inputProps={{ min: 0, step: 0.01 }} />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Controller
                  name="previewQuantity"
                  control={control}
                  rules={integerRules(1, selectedService?.maxQuantity || 1)}
                  render={({ field, fieldState }) => <TextField {...field} type="number" fullWidth label="Preview quantity" error={Boolean(fieldState.error)} helperText={fieldState.error?.message || "Display only"} inputProps={{ min: 1, max: selectedService?.maxQuantity || 1, step: 1 }} />}
                />
              </Grid>
            </Grid>

            {selectedService && (
              <Alert severity={negativeMargin ? "warning" : "success"}>
                Quantity {quantity}: customer {money(customerUnitPaise * quantity)} · provider payout {money(payoutPaise * quantity)} · CuDel gross difference {money((customerUnitPaise - payoutPaise) * quantity)}.
                {negativeMargin && " Provider payout exceeds customer price; the backend remains authoritative."}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Controller name="displayNameMode" control={control} render={({ field }) => (
                  <TextField {...field} select fullWidth label="Display name mode">
                    <MenuItem value="EXPERT">Expert</MenuItem>
                    <MenuItem value="BUSINESS">Business</MenuItem>
                    <MenuItem value="CUSTOM">Custom</MenuItem>
                  </TextField>
                )} />
              </Grid>
              {displayNameMode === "CUSTOM" && (
                <Grid item xs={12} md={8}>
                  <Controller
                    name="displayNameOverride"
                    control={control}
                    rules={{ required: "Custom display name is required", maxLength: { value: 160, message: "Maximum 160 characters" }, validate: (value) => !/[<>]/.test(value) || "Plain text only" }}
                    render={({ field, fieldState }) => <TextField {...field} fullWidth label="Custom display name" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message} inputProps={{ maxLength: 160 }} />}
                  />
                </Grid>
              )}
            </Grid>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Controller name="payoutEligible" control={control} render={({ field }) => <FormControlLabel control={<Checkbox checked={field.value} onChange={(_, checked) => field.onChange(checked)} />} label="Payout eligible" />} />
              <Controller name="active" control={control} render={({ field }) => <FormControlLabel control={<Checkbox checked={field.value} onChange={(_, checked) => field.onChange(checked)} />} label="Active mapping" />} />
              <Controller name="scheduleManagedByCudel" control={control} render={({ field }) => <FormControlLabel control={<Checkbox checked={field.value} onChange={(_, checked) => field.onChange(checked)} />} label="Schedule managed by CuDel" />} />
            </Box>

            {scheduleManagedByCudel && providerId && (
              <Alert
                severity="warning"
                action={<Button color="inherit" size="small" href={`/admin/userProfile/${providerId}/4`} target="_blank" rel="noreferrer">Edit schedule</Button>}
              >
                CuDel-managed: this is the provider’s current account-level schedule, so Admin edits affect both Quick Book and normal bookings. Provider-app schedule changes must remain backend-blocked while any active mapping owns the schedule.
              </Alert>
            )}
            {mapping && <Typography color="text.secondary" sx={{ fontSize: ".75rem" }}>Mapping version: {mapping.version}</Typography>}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button type="submit" form="quick-book-mapping-form" variant="contained" disabled={saving || loadingProvider || servicesLoading || Boolean(servicesError) || Boolean(providerServicesError) || services.length === 0 || isQuickBookMappingEditUnsafe(mapping)}>
          {saving ? <CircularProgress size={20} /> : "Save mapping"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
